import React, { useCallback, useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useLocation } from 'react-router-dom'
import { InferType } from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useAtom } from 'jotai'
import { useQuery } from '@tanstack/react-query'
import { ChatMessageBar } from '../ChatMessageBar'
import { chatMessageSchema } from '../../../forms/schemas/chat_message'
import { ChatHeader } from '../ChatHeader'
import { ChatHeaderState, chatHeaderStateAtom } from '../atom'
import { ImageActions } from '../../../actions/image-upload'
import { useUploadMedia } from '../../../hooks/useUploadMedia'
import {
  isUserAdminInSociety,
  isUserBoardMemberInSociety,
} from '../../../helpers/society'
import { useCurrentSociety } from '../../../hooks/useCurrentSociety'
import { societyQueries } from '../../../api/societies'
import { useAuth } from '../../../hooks/useAuth'
import { chatQueries } from '../../../api/chat'
import { RecipientRow } from '../../../generated/api/chat-recipients'

export const ChatRoom = (): React.ReactNode => {
  const { userId: authUserId } = useAuth()
  const [chatHeaderState] = useAtom(chatHeaderStateAtom)
  const {
    imageUploading,
    imageUploadDisabled,
    onImageRemove,
    onImagePaste,
    uploadImage,
    messageImagesDispatch,
    messageImagesState,
  } = useUploadMedia()
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const recipientIdFromQuery = queryParams.get('recipientId')
  const { society } = useCurrentSociety()

  const getDefaultValues = useMemo(() => {
    return {
      recipients: [] as RecipientRow[],
      message: '',
      authorId: authUserId as string,
      mediaIds: [],
      groupChatTitle: '',
      society: society?._id ?? '',
      isPrivate: 'true',
      isGroupchat: chatHeaderState === ChatHeaderState.StartNewInterestsChat,
      isBoardChat: false,
    }
  }, [authUserId, chatHeaderState, society?._id])

  const {
    handleSubmit,
    setValue,
    control,
    watch,
    reset,
    formState: { isValid },
  } = useForm<InferType<typeof chatMessageSchema>>({
    mode: 'all',
    resolver: yupResolver(chatMessageSchema),
    defaultValues: getDefaultValues,
  })
  const watchSociety = watch('society')
  const watchRecipients = watch('recipients')
  const watchIsGroupchat = watch('isGroupchat')

  useEffect(() => {
    setValue(
      'mediaIds',
      messageImagesState.images.flatMap((_image) =>
        _image.id ? [_image.id] : []
      ),
      { shouldValidate: true }
    )
  }, [messageImagesState, setValue])

  const { data: recipientsPage } = useQuery({
    ...chatQueries.getRecipients({
      type: 'oneonone',
      userId: recipientIdFromQuery ?? undefined,
      page: 1,
      limit: 1,
    }),
    enabled: !!recipientIdFromQuery,
  })
  const [firstRecipient] = recipientsPage?.items ?? []
  const preselectedRecipient =
    firstRecipient?.userId === recipientIdFromQuery ? firstRecipient : undefined

  // Due to the reason that we cannot populate the society value in the default values call
  // we need to populate this value at a later stage so that the dropdowns have a default value
  const { data: userSocieties } = useQuery(
    societyQueries.getUserSocieties(authUserId)
  )
  const societiesWithChatInterestsEnabled = userSocieties?.filter(
    (_society) => {
      return !_society?.settings?.chatInterestsDisabled
    }
  )
  useEffect(() => {
    if (!authUserId) return
    if (
      watchSociety === '' &&
      chatHeaderState === ChatHeaderState.StartNewChat
    ) {
      societiesWithChatInterestsEnabled?.[0]?._id &&
        setValue('society', societiesWithChatInterestsEnabled[0]?._id)
    }
    if (
      chatHeaderState === ChatHeaderState.StartNewInterestsChat &&
      !societiesWithChatInterestsEnabled?.some(
        (_society) => _society._id === watchSociety
      )
    ) {
      societiesWithChatInterestsEnabled?.[0]?._id &&
        setValue('society', societiesWithChatInterestsEnabled[0]?._id)
    }
    if (
      watchSociety === '' &&
      chatHeaderState === ChatHeaderState.StartNewBoardChat
    ) {
      const boardSocieties =
        userSocieties?.filter((_society) => {
          return (
            isUserAdminInSociety(_society, authUserId) ||
            isUserBoardMemberInSociety(_society, authUserId)
          )
        }) || []
      setValue('isBoardChat', true)
      boardSocieties[0]?._id && setValue('society', boardSocieties[0]?._id)
    }
  }, [
    authUserId,
    chatHeaderState,
    setValue,
    societiesWithChatInterestsEnabled,
    userSocieties,
    watchSociety,
  ])

  const resetForm = useCallback((): void => {
    messageImagesDispatch({ type: ImageActions.RESET })
    reset(getDefaultValues)
  }, [getDefaultValues, messageImagesDispatch, reset])

  useEffect(() => {
    resetForm()
  }, [chatHeaderState, resetForm])

  useEffect(() => {
    if (!preselectedRecipient) return
    setValue('recipients', [preselectedRecipient], { shouldValidate: true })
  }, [preselectedRecipient, setValue])

  return (
    <div className="flex min-h-0 flex-1 flex-col justify-between">
      <ChatHeader
        control={control}
        watchRecipients={watchRecipients}
        watchIsGroupchat={watchIsGroupchat || false}
        watchSociety={watchSociety}
        setValue={setValue}
      />
      <ChatMessageBar
        handleSubmit={handleSubmit}
        control={control}
        disabled={!isValid || imageUploading}
        watchRecipients={watchRecipients}
        onUploadImage={uploadImage}
        onImagePaste={onImagePaste}
        images={messageImagesState.images}
        onImageRemove={onImageRemove}
        imageUploadDisabled={imageUploadDisabled}
        resetForm={resetForm}
      />
    </div>
  )
}
